import React from 'react';
import { Grid, Paper, Typography, Box, Stack, Divider } from '@mui/material';
import {
    ShoppingBag,
    Payments,
    TrendingUp,
    Favorite,
    LocationOn,
    LocalOffer,
    Cancel,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import useCurrency from '../../../hooks/common/useCurrency';

const KpiCard = ({ icon: Icon, label, value, subValue, color = 'primary' }) => (
    <Paper
        elevation={0}
        sx={{
            p: 2.25,
            borderRadius: 3,
            border: (t) => `1px solid ${t.palette.divider}`,
            height: '100%',
            position: 'relative',
            overflow: 'hidden',
            transition: 'all 0.2s',
            '&:hover': { borderColor: (t) => t.palette[color]?.main },
        }}
    >
        <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: (t) => t.palette[color]?.main + '18',
                    color: `${color}.main`,
                    flexShrink: 0,
                }}
            >
                <Icon fontSize="small" />
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {label}
            </Typography>
        </Stack>
        <Typography
            variant="h5"
            fontWeight={700}
            sx={{ wordBreak: 'break-word', lineHeight: 1.2 }}
        >
            {value}
        </Typography>
        {subValue != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {subValue}
            </Typography>
        )}
    </Paper>
);

const SecondaryStat = ({ icon: Icon, label, value, color = 'text.secondary' }) => (
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
        <Icon sx={{ fontSize: 18, color }} />
        <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.1 }}>
                {label}
            </Typography>
            <Typography variant="body2" fontWeight={700}>
                {value}
            </Typography>
        </Box>
    </Stack>
);

const CustomerStatsRow = ({ customer }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const stats = customer?.stats || {};

    const ordersCount = stats.orders_count ?? 0;
    const cancelledCount = stats.cancelled_count ?? 0;
    const completedCount = ordersCount - cancelledCount;

    const primaryCards = [
        {
            icon: ShoppingBag,
            label: isArabic ? 'الطلبات' : 'Orders',
            value: ordersCount,
            subValue:
                cancelledCount > 0
                    ? (isArabic
                        ? `${completedCount} نشطة • ${cancelledCount} ملغاة`
                        : `${completedCount} active • ${cancelledCount} cancelled`)
                    : null,
            color: 'primary',
        },
        {
            icon: Payments,
            label: isArabic ? 'إجمالي الإنفاق' : 'Total Spent',
            value: formatPrice(stats.total_spent ?? 0),
            color: 'success',
        },
        {
            icon: TrendingUp,
            label: isArabic ? 'متوسط الطلب' : 'Avg Order Value',
            value: formatPrice(stats.avg_order_value ?? 0),
            color: 'info',
        },
        {
            icon: Cancel,
            label: isArabic ? 'الطلبات الملغاة' : 'Cancelled',
            value: cancelledCount,
            subValue:
                ordersCount > 0
                    ? `${Math.round((cancelledCount / ordersCount) * 100)}%`
                    : null,
            color: 'warning',
        },
    ];

    const secondaryStats = [
        {
            icon: Favorite,
            label: isArabic ? 'قائمة الرغبات' : 'Wishlist',
            value: stats.wishlist_count ?? 0,
            color: 'error.main',
        },
        {
            icon: LocationOn,
            label: isArabic ? 'العناوين المحفوظة' : 'Saved Addresses',
            value: stats.addresses_count ?? 0,
            color: 'primary.main',
        },
        {
            icon: LocalOffer,
            label: isArabic ? 'كوبونات مستخدمة' : 'Coupons Used',
            value: stats.coupons_redeemed_count ?? 0,
            color: 'secondary.main',
        },
    ];

    return (
        <Box sx={{ mb: 3 }}>
            {/* Primary KPI cards (always 4 across on desktop) */}
            <Grid container spacing={2}>
                {primaryCards.map((card) => (
                    <Grid key={card.label} item xs={6} md={3}>
                        <KpiCard {...card} />
                    </Grid>
                ))}
            </Grid>

            {/* Secondary stats — single horizontal strip */}
            <Paper
                elevation={0}
                sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 3,
                    border: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    divider={<Divider orientation="vertical" flexItem />}
                    spacing={{ xs: 1.5, sm: 3 }}
                    sx={{ flexWrap: 'wrap' }}
                >
                    {secondaryStats.map((s) => (
                        <SecondaryStat key={s.label} {...s} />
                    ))}
                </Stack>
            </Paper>
        </Box>
    );
};

export default CustomerStatsRow;
